import { ShieldCheck } from 'lucide-react';
import { useCallback, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable } from '../../../../components/data-table/useDataTable';
import { TextCell } from '../../../../components/table/TextCell';
import { accessLevelsApi } from '../accessLevelsApi';
import type { UserAccessLevelRow } from '../accessLevelTypes';
import { SetUserAccessLevelModal } from '../components/SetUserAccessLevelModal';

/**
 * Who reads what, for ONE module — the roster a gated module's Settings menu leads to.
 *
 * **It mirrors user management without being it.** The rows are accounts and the only thing
 * this page does to one is tag its clearance: no password, no lockout, no role, no create. An
 * administrator managing an account still goes to Administration → Users, and this page exists
 * because the question *who can read our 201 files* is asked from inside Personnel and nowhere
 * else, by somebody who should not have to read the whole account list to answer it.
 *
 * **Every account appears, tagged or not.** An untagged row is not missing information — it is
 * the account sitting at the weakest rung — and it is the row a reader opens this page to fill
 * in, so hiding it would hide the work.
 *
 * A `<DataTablePanel>` rather than a second `<IndexPage>`: the index shell brings a row-number
 * gutter, multi-select and a filter rail this list has no use for, and the panel still owes the
 * full contract — search, sort, resize and page, all server-side.
 */
export function ModuleAccessLevelsPage({
  moduleKey,
  moduleName,
  breadcrumb,
}: {
  /** A level-gated `ModuleDefinition.Key` — "personnel". */
  moduleKey: string;
  /** How the module is named on screen, for the column header and the form's sentences. */
  moduleName: string;
  breadcrumb: BreadcrumbSegment[];
}) {
  /*
    Gated on BOTH keys, and the hook is told not to fetch at all without them — a caller who
    may not read accounts gets no request rather than a 403 behind an empty table, and a caller
    who cannot tag gets no page, because tagging is the whole of what it does.
  */
  const canRead = usePermission('users.read');
  const canAssign = usePermission('users.assign-levels');
  const enabled = canRead && canAssign;

  const [tagging, setTagging] = useState<UserAccessLevelRow | null>(null);

  // `useCallback`-stable, or the panel's effect re-runs on every render.
  const fetchPage = useCallback(
    (
      query: { search?: string; page: number; pageSize: number; sort?: string },
      signal: AbortSignal,
    ) =>
      accessLevelsApi.roster(
        moduleKey,
        {
          search: query.search ?? '',
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          // The resting state hides deactivated accounts, like every other list here. There is
          // no Show deleted: tagging an account nobody can sign in as is not a job.
          status: 'active',
        },
        signal,
      ),
    [moduleKey],
  );

  const table = useDataTable<UserAccessLevelRow>({
    fetchPage,
    enabled,
    defaultSort: 'displayName',
  });

  const columns: DataColumn<UserAccessLevelRow>[] = [
    {
      key: 'displayName',
      header: 'Account',
      width: 220,
      sortable: true,
      render: (row) => <TextCell value={row.displayName} />,
    },
    {
      key: 'email',
      header: 'Email',
      width: 260,
      sortable: true,
      render: (row) => <TextCell value={row.email} />,
    },
    {
      key: 'roleName',
      header: 'Role',
      width: 160,
      render: (row) => <TextCell value={row.roleName} />,
    },
    {
      /*
        The level, with its rank beside it on the same line under one header — the anchor rule
        applied to a value that is meaningless without its number. "Level 2" alone does not say
        which direction the ladder runs.

        The column KEY is `rank`, because that is what the server orders by: a key the sort
        allow-list does not know falls back silently, which reads as a broken control. Sorting
        ascending brings the strongest clearances first and leaves the untagged accounts last.

        An untagged account renders an em dash rather than an invented word: the account has no
        level, which is a different statement from any rung the ladder defines.
      */
      key: 'rank',
      header: `${moduleName} level`,
      width: 200,
      sortable: true,
      render: (row) =>
        row.accessLevelName != null ? (
          <TextCell value={`${row.accessLevelName} — rank ${row.rank}`} />
        ) : (
          <TextCell value={null} />
        ),
    },
    /*
      NO STATUS COLUMN, and that is the pair to the `status: 'active'` above: this page asks
      only for live accounts, so a status column could carry one value on every row forever.
      It owes one the moment it grows a Show deleted — a panel that can show deleted rows
      without a status column is the easier half of that pair to forget.
    */
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      {/* The breadcrumb IS the back-nav, so Settings links; this page ships no Back button. */}
      <Breadcrumb segments={breadcrumb} />

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-5xl flex-col gap-4">
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-start gap-3">
              <span
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700"
                aria-hidden="true"
              >
                <ShieldCheck className="h-4 w-4" />
              </span>

              {/*
                No heading: the breadcrumb already names this page. The sentence is not a second
                name — it says which way the ladder runs, which is the one thing a reader has to
                get right before changing anything on this screen.
              */}
              <p className="min-w-0 text-xs text-slate-500">
                Which {moduleName.toLowerCase()} records each account can read. A stronger rung
                reads more: rank 1 reads everything, and an account at rank 2 reads rank 2 and
                weaker and never rank 1. An account with no level reads unclassified records and
                the weakest rung only — and still sees every record counted in the list.
              </p>
            </div>

            {enabled ? (
              <DataTablePanel
                id={`${moduleKey}-access-levels`}
                state={table}
                columns={columns}
                rowKey={(row) => row.userId}
                searchPlaceholder="Search accounts"
                emptyMessage="No accounts yet."
                emptySearchMessage="No account matches that search."
                minWidth={900}
                // Nothing is ever deleted from this list, so the shell's bulk machinery would
                // be a gutter of checkboxes over a page with no bulk action behind it.
                selectable={false}
                /*
                  Edit is the canonical prop rather than a `rowActions` entry, so the menu keeps
                  the order and icons every other row in the product has. What it opens is the
                  level picker, which is the only thing this page writes.
                */
                onEdit={(row) => setTagging(row)}
                editPermission="users.assign-levels"
              />
            ) : null}
          </section>
        </div>
      </div>

      {tagging ? (
        <SetUserAccessLevelModal
          open
          userId={tagging.userId}
          displayName={tagging.displayName}
          moduleKey={moduleKey}
          moduleName={moduleName}
          currentLevelId={tagging.accessLevelId ?? null}
          onClose={() => setTagging(null)}
          onSaved={() => {
            setTagging(null);
            table.reload();
          }}
        />
      ) : null}
    </div>
  );
}
